import React from "react";

// glonet.ai app theme — the landing page's design language as shared tokens,
// used by the screens converted in the 2026-07 "MML App Redesign" pass.
// Every token is a CSS variable reference so a single data attribute on
// <html> (data-ga-theme="dark" | "light") flips every converted screen at
// once. The palettes themselves live in GA_GLOBAL_CSS below (injected by
// GaStyle). Tailwind stays for layout/spacing; colors ride these tokens.
export const GA = {
  bg: "var(--ga-bg)",
  panel: "var(--ga-panel)",
  panelBorder: "var(--ga-panel-border)",
  soft: "var(--ga-soft)",
  softer: "var(--ga-softer)",
  inputBg: "var(--ga-input-bg)",
  inputBorder: "var(--ga-input-border)",
  text: "var(--ga-text)",
  body: "var(--ga-body)",
  muted: "var(--ga-muted)",
  faint: "var(--ga-faint)",
  accent: "var(--ga-accent)",
  accentStrong: "var(--ga-accent-strong)",
  green: "var(--ga-green)",
  danger: "var(--ga-danger)",
  red: "var(--ga-red)",
  // Text/icon color on accent-filled pills (dark bg color in dark theme,
  // white in light theme).
  onAccent: "var(--ga-on-accent)",
  // Literal families used across the converted screens.
  overlay: "var(--ga-overlay)",
  ghostBorder: "var(--ga-ghost-border)",
  navBg: "var(--ga-nav-bg)",
  accentWash: "var(--ga-accent-wash)",
  greenWash: "var(--ga-green-wash)",
  greenBorder: "var(--ga-green-border)",
  redWash: "var(--ga-red-wash)",
  redBorder: "var(--ga-red-border)",
  amberWash: "var(--ga-amber-wash)",
  amberBorder: "var(--ga-amber-border)",
  skel: "var(--ga-skel)",
  track: "var(--ga-track)",
  mono: "'JetBrains Mono',monospace",
  serif: "'Instrument Serif',serif",
  sans: "'Hanken Grotesk',system-ui,sans-serif",
};

// The two palettes. Dark is the exact pre-variable palette; light is the
// same scheme on lighter surfaces.
const GA_DARK_PALETTE = `
  --ga-bg: #0c0d10;
  --ga-panel: #16181d;
  --ga-panel-border: rgba(255,255,255,0.08);
  --ga-soft: rgba(255,255,255,0.03);
  --ga-softer: rgba(255,255,255,0.05);
  --ga-input-bg: rgba(255,255,255,0.04);
  --ga-input-border: rgba(255,255,255,0.14);
  --ga-text: #f1f2f4;
  --ga-body: #b6b9c0;
  --ga-muted: #7c818b;
  --ga-faint: #6b6f78;
  --ga-accent: #74a0f5;
  --ga-accent-strong: #5b8cf0;
  --ga-green: #57d9a4;
  --ga-danger: #e0a35e;
  --ga-red: #e57373;
  --ga-on-accent: #0c0d10;
  --ga-overlay: rgba(0,0,0,0.65);
  --ga-ghost-border: rgba(255,255,255,0.18);
  --ga-nav-bg: rgba(12,13,16,0.92);
  --ga-accent-wash: rgba(116,160,245,0.12);
  --ga-green-wash: rgba(87,217,164,0.12);
  --ga-green-border: rgba(87,217,164,0.4);
  --ga-red-wash: rgba(229,115,115,0.12);
  --ga-red-border: rgba(229,115,115,0.4);
  --ga-amber-wash: rgba(224,163,94,0.12);
  --ga-amber-border: rgba(224,163,94,0.4);
  --ga-skel: rgba(255,255,255,0.08);
  --ga-track: rgba(255,255,255,0.12);
  --ga-card-hover-border: rgba(116,160,245,0.55);
`;

const GA_LIGHT_PALETTE = `
  --ga-bg: #f4f5f8;
  --ga-panel: #ffffff;
  --ga-panel-border: rgba(12,13,16,0.10);
  --ga-soft: rgba(12,13,16,0.035);
  --ga-softer: rgba(12,13,16,0.05);
  --ga-input-bg: rgba(12,13,16,0.04);
  --ga-input-border: rgba(12,13,16,0.16);
  --ga-text: #16181d;
  --ga-body: #3f4450;
  --ga-muted: #6b7280;
  --ga-faint: #8a8f98;
  --ga-accent: #5b8cf0;
  --ga-accent-strong: #4a7de8;
  --ga-green: #1f9e6e;
  --ga-danger: #b97a2e;
  --ga-red: #c74f4f;
  --ga-on-accent: #ffffff;
  --ga-overlay: rgba(20,24,40,0.45);
  --ga-ghost-border: rgba(12,13,16,0.18);
  --ga-nav-bg: rgba(255,255,255,0.92);
  --ga-accent-wash: rgba(91,140,240,0.12);
  --ga-green-wash: rgba(31,158,110,0.12);
  --ga-green-border: rgba(31,158,110,0.4);
  --ga-red-wash: rgba(199,79,79,0.10);
  --ga-red-border: rgba(199,79,79,0.4);
  --ga-amber-wash: rgba(185,122,46,0.12);
  --ga-amber-border: rgba(185,122,46,0.4);
  --ga-skel: rgba(12,13,16,0.08);
  --ga-track: rgba(12,13,16,0.12);
  --ga-card-hover-border: rgba(91,140,240,0.55);
`;

// Theme switching: "ga-theme" in localStorage ("dark" | "light"), stamped on
// <html data-ga-theme="...">. Dark is the default.
export function initGaTheme() {
  let theme = "dark";
  try {
    if (window.localStorage.getItem("ga-theme") === "light") theme = "light";
  } catch (e) { /* storage unavailable — stay dark */ }
  document.documentElement.dataset.gaTheme = theme;
  return theme;
}

export function toggleGaTheme() {
  const next =
    document.documentElement.dataset.gaTheme === "light" ? "dark" : "light";
  document.documentElement.dataset.gaTheme = next;
  try {
    window.localStorage.setItem("ga-theme", next);
  } catch (e) { /* storage unavailable — theme still flips for the session */ }
  return next;
}

export function getGaTheme() {
  return document.documentElement.dataset.gaTheme === "light" ? "light" : "dark";
}

// Stamp the theme at module load so the first paint already has the palette.
initGaTheme();

// The landing's surfaces, as ready-to-spread style objects.
export const gaCard = {
  background: GA.panel,
  border: `1px solid ${GA.panelBorder}`,
  borderRadius: 16,
  color: GA.text,
};

export const gaSoftCard = {
  background: GA.soft,
  border: `1px solid ${GA.panelBorder}`,
  borderRadius: 18,
  color: GA.text,
};

export const gaInput = {
  background: GA.inputBg,
  border: `1px solid ${GA.inputBorder}`,
  borderRadius: 10,
  color: GA.text,
  outline: "none",
};

export const gaBtnPrimary = {
  fontFamily: "inherit",
  fontSize: 15,
  fontWeight: 600,
  color: GA.onAccent,
  background: GA.accent,
  border: "none",
  padding: "12px 22px",
  borderRadius: 999,
  cursor: "pointer",
};

export const gaBtnGhost = {
  fontFamily: "inherit",
  fontSize: 15,
  fontWeight: 500,
  color: GA.text,
  background: "transparent",
  border: `1px solid ${GA.ghostBorder}`,
  padding: "12px 20px",
  borderRadius: 999,
  cursor: "pointer",
};

// Page heading pair: mono kicker over a serif title (the landing's rhythm).
export function GaPageTitle({ kicker, title, center = true, className = "" }) {
  return (
    <div className={className} style={{ textAlign: center ? "center" : "left", marginBottom: 28 }}>
      {kicker && (
        <div style={{ fontFamily: GA.mono, fontSize: 12, letterSpacing: "0.26em",
                      color: GA.accent, textTransform: "uppercase", marginBottom: 12 }}>
          {kicker}
        </div>
      )}
      <h1 style={{ fontFamily: GA.serif, fontWeight: 400, fontSize: "clamp(30px,4.4vw,44px)",
                   lineHeight: 1.05, letterSpacing: "-0.015em", color: GA.text, margin: 0 }}>
        {title}
      </h1>
    </div>
  );
}

// Palette definitions + shared hover/focus affordances (mirrors the
// landing's <style> block). Plain :root fallback = dark, so anything that
// renders before initGaTheme() stamps the attribute still gets the palette.
export const GA_GLOBAL_CSS = `
  :root { ${GA_DARK_PALETTE} }
  :root[data-ga-theme="dark"] { ${GA_DARK_PALETTE} }
  :root[data-ga-theme="light"] { ${GA_LIGHT_PALETTE} }
  .ga-link { color:var(--ga-body); text-decoration:none; transition:color .15s; }
  .ga-link:hover { color:var(--ga-text); }
  .ga-btn { transition:background .15s, border-color .15s, color .15s; }
  .ga-btn-primary:hover { background:var(--ga-accent-strong) !important; }
  .ga-btn-ghost:hover { border-color:var(--ga-accent) !important; }
  .ga-input:focus { border-color:var(--ga-accent) !important; }
  .ga-card-hover { transition:border-color .15s, transform .15s; }
  .ga-card-hover:hover { border-color:var(--ga-card-hover-border); transform:translateY(-1px); }
`;

export function GaStyle() {
  return <style>{GA_GLOBAL_CSS}</style>;
}
